// En direct: the city map with every rider's last position (MyGaz Pro sends
// it every 10 s on a run) and the open orders' homes, plus the riders' list.
// Every point on the map has a card on hover and acts on a click (LiveMap).
import { useMemo, useState } from 'react';
import { S, LANG } from '../lib/strings';
import { discColor, hhmm, initials, minutesBetween, rel } from '../lib/format';
import { statusPill } from '../lib/orders';
import type { Live as LiveData, OpsOrder } from '../lib/types';
import { LiveMap } from '../components/LiveMap';
import { Ic } from '../components/Icon';
import { Spinner } from '../components/Busy';
import type { Sync } from '../lib/useData';

type F = 'all' | 'busy' | 'free';

export function Live({ live, updatedAt, sync, openOrder, openHome, orders }: { live: LiveData | null; updatedAt: Date | null; sync: Sync; openOrder(id: string): void; openHome(id: string): void; orders: OpsOrder[] | null }) {
  const [f, setF] = useState<F>('all');
  const [sel, setSel] = useState<string | null>(null);
  const riders = useMemo(() => (live?.riders ?? []).filter((r) => f === 'all' || (f === 'busy' ? !!r.order : !r.order)), [live, f]);
  const on = (live?.riders ?? []).filter((r) => r.live).length;
  return (
    <section className="view on" aria-label={S.tabs.live}>
      <div className="livewrap">
        <LiveMap live={live} onRider={setSel} onHome={openHome} onOrder={openOrder} orders={orders} padding={80} inset={356} />
        <div className="lpanel glass">
          <div className="lh">
            <h2><span className="livedot" />{S.lv.title}</h2>
            <p>{S.lv.sub(on, updatedAt ? hhmm(updatedAt) : '—')}</p>
            <div className="seg" role="group">
              {(['all', 'busy', 'free'] as F[]).map((k) => <button key={k} aria-pressed={f === k} onClick={() => setF(k)}>{S.lv[k]}</button>)}
            </div>
          </div>
          <div className="rlist">
            {riders.length ? riders.map((r) => {
              const st = r.order ? S.lv.status.busy : r.live ? S.lv.status.free : S.lv.status.stale;
              const dot = r.order ? 'var(--blue)' : r.live ? 'var(--green)' : 'var(--ink-4)';
              return (
                <button key={r.id} className={'ri' + (sel === r.id ? ' sel' : '')} onClick={() => { setSel(r.id); if (r.order) openOrder(r.order.id); }}>
                  <span className="disc" style={{ background: discColor(r.id) }}>{initials(r.name)}<span className="sd" style={{ background: dot }} /></span>
                  <span style={{ minWidth: 0 }}>
                    <b>{r.name}</b>
                    <span className="sub">{r.order ? `${r.order.ref} · ${r.order.customer} · ${r.order.quartier}` : `${st} · ${r.agency}`}</span>
                  </span>
                  <span className="eta">
                    {r.order ? <span className={'st ' + statusPill(r.order.status)[1]}>{statusPill(r.order.status)[0]}</span>
                      : <span className="muted" style={{ fontSize: 11.5 }}>{r.at ? S.lv.seen(rel(minutesBetween(r.at), LANG)) : ''}</span>}
                  </span>
                </button>
              );
            }) : <div className="empty"><b>{S.lv.none}</b>{S.lv.noneSub}</div>}
          </div>
        </div>
        <div className="ltop"><span /><div className={'glass livepill' + (sync.failed ? ' bad' : '')} title={sync.failed && updatedAt ? S.ld.notSince(hhmm(updatedAt)) : undefined}>
          <Ic n={sync.failed ? 'alert' : 'clock'} s="xs" /><span className={'fresh' + (sync.refreshing ? ' busy' : '')}>{updatedAt ? hhmm(updatedAt) : '—'}<Spinner s={11} /></span>
          {sync.failed && <button className="link" onClick={sync.retry}>{S.errors.retry}</button>}</div></div>
      </div>
    </section>
  );
}
